/**
 * @file useNavBarSearch.js
 * @description 导航栏搜索：关键词跳转与登录用户搜索记录
 * @module shared/composables/useNavBarSearch
 */

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  clearSearchHistoryApi,
  deleteSearchHistoryApi,
  fetchSearchHistoryPageApi,
} from '@/features/user/api/userApi'

/** @typedef {import('@/features/user/api/userApi').SearchHistoryItem} SearchHistoryItem */

const PAGE_SIZE = 5
const MAX_PAGE_NUM = 2

/**
 * @param {{ authToken: string }} props
 */
export function useNavBarSearch(props) {
  const router = useRouter()

  const searchText = ref('')
  const isFocused = ref(false)
  const isDropdownVisible = ref(false)
  /** @type {import('vue').Ref<SearchHistoryItem[]>} */
  const historyList = ref([])
  const historyTotal = ref(0)
  const historyLoading = ref(false)
  const loadedPageNum = ref(0)

  const searchRootRef = ref(null)
  let blurTimer = null
  let docClickHandler = null
  /** @type {Promise<void>|null} */
  let refreshHistoryTask = null

  const hasToken = computed(() => Boolean(resolveToken()))

  const canLoadMore = computed(() => {
    if (historyTotal.value <= PAGE_SIZE) return false
    return historyList.value.length < historyTotal.value && loadedPageNum.value < MAX_PAGE_NUM
  })

  const showHistoryFooter = computed(() => historyTotal.value > PAGE_SIZE)

  const showClearAll = computed(() => historyTotal.value > PAGE_SIZE)

  function resolveToken() {
    return (
      props.authToken ||
      JSON.parse(localStorage.getItem('user') || '{}')?.token ||
      ''
    )
  }

  function isSuccessCode(code) {
    return code === 0 || code === 200
  }

  /**
   * 拉取搜索记录（最多两页）
   * @param {number} pageNum
   * @param {boolean} [append]
   */
  async function fetchHistory(pageNum = 1, append = false) {
    const token = resolveToken()
    if (!token) {
      historyList.value = []
      historyTotal.value = 0
      loadedPageNum.value = 0
      return
    }

    historyLoading.value = true
    try {
      const res = await fetchSearchHistoryPageApi(
        { pageNum, pageSize: PAGE_SIZE },
        token
      )
      if (isSuccessCode(res.data.code)) {
        const contents = Array.isArray(res.data.contents) ? res.data.contents : []
        historyTotal.value = Number(res.data.total) || contents.length
        historyList.value = append ? [...historyList.value, ...contents] : contents
        loadedPageNum.value = pageNum
        if (!append && historyList.value.length === 0) {
          closeDropdown()
          unbindDocClickOutside()
        }
      }
    } catch (e) {
      console.error('获取搜索记录失败', e)
      closeDropdown()
      unbindDocClickOutside()
    } finally {
      historyLoading.value = false
    }
  }

  async function refreshHistoryDropdown() {
    if (!hasToken.value) return

    if (!refreshHistoryTask) {
      refreshHistoryTask = (async () => {
        await fetchHistory(1)

        if (historyList.value.length > 0) {
          isDropdownVisible.value = true
          bindDocClickOutside()
        } else {
          closeDropdown()
          unbindDocClickOutside()
        }
      })().finally(() => {
        refreshHistoryTask = null
      })
    }

    await refreshHistoryTask
  }

  function closeDropdown() {
    isDropdownVisible.value = false
  }

  function handleFocus() {
    isFocused.value = true
    clearBlurTimer()
    void refreshHistoryDropdown()
  }

  /**
   * 已聚焦时再次点击搜索条，重新拉取记录
   * @param {MouseEvent} event
   */
  function handleBarClick(event) {
    if (event.target instanceof Element && event.target.closest('.navbar-search__submit')) {
      return
    }
    clearBlurTimer()
    isFocused.value = true
    void refreshHistoryDropdown()
  }

  function handleBlur() {
    isFocused.value = false
    clearBlurTimer()
    blurTimer = setTimeout(() => {
      closeDropdown()
      unbindDocClickOutside()
    }, 160)
  }

  function clearBlurTimer() {
    if (blurTimer) {
      clearTimeout(blurTimer)
      blurTimer = null
    }
  }

  function bindDocClickOutside() {
    unbindDocClickOutside()
    docClickHandler = (event) => {
      const root = searchRootRef.value
      if (root && !root.contains(event.target)) {
        closeDropdown()
        unbindDocClickOutside()
      }
    }
    document.addEventListener('mousedown', docClickHandler)
  }

  function unbindDocClickOutside() {
    if (docClickHandler) {
      document.removeEventListener('mousedown', docClickHandler)
      docClickHandler = null
    }
  }

  function keepDropdownOpen() {
    clearBlurTimer()
    isFocused.value = true
  }

  /**
   * 执行关键词搜索并跳转
   * @param {string} [keyword]
   */
  function performSearch(keyword) {
    const value = (keyword ?? searchText.value).trim()
    if (!value) return

    searchText.value = value
    closeDropdown()
    router.push({
      name: 'ComicSearch',
      query: {
        mode: 'search',
        keyword: value,
      },
    })
  }

  /**
   * 点击历史记录项
   * @param {SearchHistoryItem} item
   */
  function selectHistoryItem(item) {
    if (!item?.keyword) return
    searchText.value = item.keyword
    performSearch(item.keyword)
  }

  /**
   * 删除单条搜索记录
   * @param {SearchHistoryItem} item
   */
  async function deleteHistoryItem(item) {
    const token = resolveToken()
    if (!token || !item?.id) return

    keepDropdownOpen()
    try {
      const res = await deleteSearchHistoryApi(item.id, token)
      if (isSuccessCode(res.data.code)) {
        historyList.value = historyList.value.filter((row) => row.id !== item.id)
        historyTotal.value = Math.max(0, historyTotal.value - 1)
        if (historyList.value.length === 0) {
          closeDropdown()
        }
      }
    } catch (e) {
      console.error('删除搜索记录失败', e)
    }
  }

  async function loadMoreHistory() {
    if (!canLoadMore.value || historyLoading.value) return
    keepDropdownOpen()
    await fetchHistory(loadedPageNum.value + 1, true)
  }

  async function clearAllHistory() {
    const token = resolveToken()
    if (!token) return

    keepDropdownOpen()
    try {
      const res = await clearSearchHistoryApi(token)
      if (isSuccessCode(res.data.code)) {
        historyList.value = []
        historyTotal.value = 0
        loadedPageNum.value = 0
        closeDropdown()
      }
    } catch (e) {
      console.error('清空搜索记录失败', e)
    }
  }

  watch(
    () => props.authToken,
    (val) => {
      historyList.value = []
      historyTotal.value = 0
      loadedPageNum.value = 0
      if (!val) {
        closeDropdown()
      }
    }
  )

  onBeforeUnmount(() => {
    clearBlurTimer()
    unbindDocClickOutside()
  })

  return {
    searchText,
    isFocused,
    isDropdownVisible,
    historyList,
    historyTotal,
    historyLoading,
    hasToken,
    canLoadMore,
    showHistoryFooter,
    showClearAll,
    searchRootRef,
    handleFocus,
    handleBlur,
    handleBarClick,
    keepDropdownOpen,
    performSearch,
    selectHistoryItem,
    deleteHistoryItem,
    loadMoreHistory,
    clearAllHistory,
    refreshHistory: () => refreshHistoryDropdown(),
  }
}
